/* eslint-disable max-len */
import React, { useCallback, useMemo } from 'react'
import { useDropzone } from 'react-dropzone'
import type { DropEvent, FileRejection } from 'react-dropzone'
import { useDispatch } from 'react-redux'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import FormHelperText from '@material-ui/core/FormHelperText'

import { FileObject } from './dropzone-area.types'
import {
  convertBytesToMbsOrKbs,
  formatFileObject,
  readFile
} from './dropzone-area.utils'
import { useDropzoneAreaStyles } from './dropzone-area.styles'
import {
  pushDangerNotification,
  pushInfoNotification,
  pushSuccessNotification
} from '../snackbar'
import { DropzoneAreaPreviewList } from './dropzone-area-preview-list'
import { TranslationDictionary } from '../../utils'

type DropzoneAreaProps = {
  acceptedFiles?: string[]
  onDropAccepted?: (acceptedFiles: File[], event: DropEvent) => void
  onDropRejected?: (rejectedFiles: FileRejection[], event: DropEvent) => void
  onAdd?: (fileObjs: FileObject[]) => void
  onDelete?: (removeFileObj: FileObject, index: number) => void
  filesLimit?: number
  getFileLimitExceedMessage?: (filesLimit: number, locale: string) => string
  getFileAddedMessage?: (fileName: string, locale: string) => string
  getFileRemovedMessage?: (fileName: string, locale: string) => string
  getDropRejectMessage?: (
    rejectedFile: File,
    acceptedFiles: string[],
    maxFileSize: number,
    locale: string
  ) => string
  fileObjects?: string | string[] | FileObject | FileObject[]
  maxFileSize?: number
  previewVideo?: boolean
  previewFile3D?: boolean
  disabled?: boolean
  error?: boolean
  disableRejectionFeedback?: false
  inputProps?: React.HTMLProps<HTMLInputElement>
  dropzoneText?: string
  showPreviews?: boolean
  helperText?: string
  showFileNames?: boolean
  getFileName?: (file: FileObject, index: number) => string
  getFileUrl?: (file: FileObject, index: number) => string
  previewText?: string
  previewImg?: boolean
  translate?: TranslationDictionary['data']
  locale: string
}

export const DropzoneArea: React.FC<DropzoneAreaProps> = ({
  filesLimit = 10,
  getFileLimitExceedMessage,
  getFileAddedMessage,
  getDropRejectMessage,
  getFileRemovedMessage,
  onDropAccepted,
  onDropRejected,
  onAdd,
  onDelete,
  maxFileSize = 10000000,
  disabled,
  disableRejectionFeedback,
  acceptedFiles = ['image/jpeg', 'image/png'],
  previewImg = true,
  previewVideo,
  previewFile3D,
  translate,
  locale,
  ...props
}) => {
  const classes = useDropzoneAreaStyles()
  const dispatch = useDispatch()
  const fileObjects = useMemo(() => {
    if (!props.fileObjects) {
      return []
    }

    if (Array.isArray(props.fileObjects)) {
      return props.fileObjects.map((fileObj) => formatFileObject(fileObj))
    }

    return [formatFileObject(props.fileObjects)]
  }, [props.fileObjects])

  const handleDropAccepted = useCallback(
    async (acceptedFiles: File[], event: DropEvent) => {
      const isFileLimitExceed =
        filesLimit > 1 && fileObjects.length + acceptedFiles.length > filesLimit

      if (isFileLimitExceed) {
        dispatch(
          pushDangerNotification(
            getFileLimitExceedMessage?.(filesLimit, locale) ?? ''
          )
        )
        return
      }

      if (onDropAccepted) {
        onDropAccepted(acceptedFiles, event)
      }

      const fileObjs = await Promise.all(
        acceptedFiles.map(async (file) => {
          const data = await readFile(file)

          return {
            file,
            data
          }
        })
      )

      if (onAdd) {
        onAdd(fileObjs)
      }

      fileObjs.forEach((fileObj) =>
        dispatch(
          pushSuccessNotification(
            getFileAddedMessage?.(fileObj.file.name, locale) ?? ''
          )
        )
      )
    },
    [
      dispatch,
      getFileLimitExceedMessage,
      filesLimit,
      fileObjects,
      onDropAccepted,
      locale,
      onAdd,
      getFileAddedMessage
    ]
  )

  const handleDropReject = useCallback(
    (rejectedFiles: FileRejection[], event: DropEvent) => {
      const isFileLimitExceed =
        fileObjects.length + rejectedFiles.length > filesLimit

      let message: string | undefined = ''

      if (isFileLimitExceed) {
        message = getFileLimitExceedMessage?.(filesLimit, locale)
      } else {
        rejectedFiles.forEach((rejectedFile) => {
          message = getDropRejectMessage?.(
            rejectedFile.file,
            acceptedFiles,
            maxFileSize,
            locale
          )
        })
      }

      if (onDropRejected) {
        onDropRejected(rejectedFiles, event)
      }

      if (message) {
        dispatch(pushDangerNotification(message))
      }
    },
    [
      acceptedFiles,
      dispatch,
      fileObjects.length,
      filesLimit,
      getDropRejectMessage,
      getFileLimitExceedMessage,
      locale,
      maxFileSize,
      onDropRejected
    ]
  )

  const handleRemove = useCallback(
    (removedFileObj: FileObject, index: number) =>
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation()

        if (onDelete) {
          onDelete(removedFileObj, index)
        }

        if (removedFileObj?.file?.name) {
          dispatch(
            pushInfoNotification(
              getFileRemovedMessage?.(removedFileObj?.file?.name, locale) ?? ''
            )
          )
        }
      },
    [dispatch, getFileRemovedMessage, locale, onDelete]
  )

  const acceptFiles = acceptedFiles?.join(',')
  const isMultiple = filesLimit > 1
  const previewsVisible = props.showPreviews && fileObjects.length > 0

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDropAccepted: handleDropAccepted,
      onDropRejected: handleDropReject,
      maxSize: maxFileSize,
      accept: acceptFiles,
      multiple: isMultiple,
      disabled
    })

  return (
    <>
      <div
        {...getRootProps({
          className: clsx(classes.root, {
            [classes.active]: isDragActive,
            [classes.invalid]: !disableRejectionFeedback && isDragReject,
            [classes.hideDragzone]: !isMultiple
              ? fileObjects.length >= 1
              : filesLimit && fileObjects.length >= filesLimit,
            [classes.error]: props.error
          })
        })}
        style={{ marginBottom: props.helperText && props.error ? 0 : 23 }}
      >
        <input {...getInputProps(props.inputProps)} />

        <div className={classes.textContainer}>
          <Typography variant="h5" component="p" className={classes.text}>
            {props.dropzoneText}
          </Typography>

          <CloudUploadIcon className={classes.icon} />
        </div>
      </div>
      {props.helperText && props.error && (
        <FormHelperText
          error={Boolean(props.error)}
          className={classes.helperText}
        >
          {props.helperText}
        </FormHelperText>
      )}
      {previewsVisible && (
        <DropzoneAreaPreviewList
          onDelete={handleRemove}
          fileObjects={fileObjects}
          showFileNames={props.showFileNames}
          getFileName={props.getFileName}
          getFileUrl={props.getFileUrl}
          previewText={props.previewText}
          previewImg={previewImg}
          translate={translate}
          previewVideo={previewVideo}
          previewFile3D={previewFile3D}
        />
      )}
    </>
  )
}

DropzoneArea.defaultProps = {
  fileObjects: [],
  disabled: false,
  disableRejectionFeedback: false,
  previewText: 'Предпросмотр',
  showPreviews: true,
  showFileNames: true,
  getFileLimitExceedMessage: (filesLimit: number, locale: string) => {
    return locale === 'en'
      ? `The maximum number of files allowed has been exceeded. Only ${filesLimit} is allowed`
      : `Превышено максимально допустимое количество файлов. Разрешено только ${filesLimit}`
  },
  getFileAddedMessage: (fileName: string, locale: string) => {
    return locale === 'en'
      ? `File ${fileName} added successfully`
      : `Файл ${fileName} успешно добавлен`
  },
  getFileRemovedMessage: (fileName: string, locale: string) => {
    return locale === 'en'
      ? `File ${fileName} was deleted`
      : `Файл ${fileName} был удален`
  },
  getDropRejectMessage: (rejectedFile, acceptedFiles, maxFileSize, locale) => {
    let message =
      locale === 'en'
        ? `File ${rejectedFile.name} was not loaded. `
        : `Файл ${rejectedFile.name} не был загружен. `

    if (!acceptedFiles.includes(rejectedFile.type)) {
      message +=
        locale === 'en'
          ? 'File type not supported'
          : 'Тип файла не поддерживается'
    }

    if (rejectedFile.size > maxFileSize) {
      message +=
        locale === 'ru'
          ? 'Файл слишком большой. Ограничение по размеру ' +
            convertBytesToMbsOrKbs(maxFileSize, locale)
          : 'The file is too large. Size limit ' +
            convertBytesToMbsOrKbs(maxFileSize, locale)
    }
    return message
  }
}
